import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import CalculadoraDOT from '../CalculadoraDOT';
import ToolCta from '../ToolCta';
import { fadeInUp } from '../../../lib/animations';

const TOOL_SLUG = 'validar-pneu-dot';

/** Página /ferramentas/validar-pneu-dot */
const DotPage: React.FC = () => (
  <>
    <Helmet>
      <title>Verificar Validade do Pneu pelo Código DOT – Calculadora Grátis | Gas2</title>
      <meta
        name="description"
        content="Descubra a data de fabricação e a validade do seu pneu pelo código DOT em segundos. Saiba quando trocar pneu ressecado e evite acidentes. Calculadora gratuita."
      />
      <link rel="canonical" href={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta
        property="og:title"
        content="Verificar Validade do Pneu pelo Código DOT – Gas2"
      />
      <meta
        property="og:description"
        content="Calculadora gratuita de validade do pneu pelo código DOT: data de fabricação, troca recomendada e descarte obrigatório."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Verificar Validade do Pneu pelo Código DOT – Gas2"
      />
      <meta
        name="twitter:description"
        content="Calculadora gratuita: descubra a data de fabricação e validade do pneu pelo código DOT."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── H1 + intro ── */}
        <motion.header
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            Segurança Veicular
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Calculadora de Validade do Pneu pelo Código DOT
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            Digite os <strong className="text-slate-200">4 últimos dígitos</strong> do seu
            código DOT e descubra a data de fabricação, quando é recomendada a troca e o
            limite de descarte obrigatório.
          </p>
        </motion.header>

        {/* ── Calculadora ── */}
        <CalculadoraDOT />

        {/* ── Conteúdo SEO ── */}
        <motion.article
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="prose prose-invert prose-sm sm:prose-base max-w-none space-y-8"
        >
          <section>
            <h2 className="font-display text-2xl font-bold text-white mt-0">
              O que é o Código DOT do Pneu?
            </h2>
            <p className="text-slate-400 leading-relaxed">
              DOT significa <em>Department of Transportation</em>, a agência norte-americana
              de transporte. Todo pneu fabricado a partir de 2000 carrega um código DOT
              gravado na lateral (flanco), que identifier o fabricante, o tamanho e,
              nos <strong className="text-slate-200">4 últimos dígitos</strong>, a semana e
              o ano de fabricação. Exemplo: <strong className="text-slate-200">1423</strong>{' '}
              = 14ª semana de 2023.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Como Identificar o Código DOT no Pneu?
            </h2>
            <p className="text-slate-400 leading-relaxed">
              O código completo fica gravado na lateral interna do pneu, geralmente no
              flanco oposto ao que fica visível quando montado. Procure a sequência que começa
              com as letras <strong className="text-slate-200">DOT</strong> seguida de letras
              e números. Apenas os <strong className="text-slate-200">4 dígitos finais</strong>{' '}
              interessam para calcular a validade — os demais identificam a fábrica e o molde.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Quando Devo Trocar o Pneu Ressecado?
            </h2>
            <p className="text-slate-400 leading-relaxed">
              Fabricantes como Michelin, Bridgestone e Continental recomendam a substituição
              dos pneus a partir de <strong className="text-slate-200">5 anos</strong> de
              fabricação, mesmo que pareçam intactos. A borracha envelhece internamente por
              oxidação e exposição ao calor, perdendo elasticidade e resistência ao impacto.
              Após <strong className="text-slate-200">10 anos</strong> o descarte é obrigatório
              por lei em vários países e fortemente recomendado no Brasil, independentemente
              do aspecto visual.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              Sinais visuais de alerta: trincas na lateral ou entre os sulcos, superfície
              áspera ao toque, deformações visíveis e perda frequente de pressão sem furo
              aparente. Qualquer um desses sintomas indica substituição imediata,
              independentemente da idade pelo DOT.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Perguntas Frequentes sobre Validade de Pneu
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: 'Pneu novo de estoque ainda é bom?',
                  a: 'Sim, desde que o DOT seja recente. Pneus ficam em estoque por até 2 anos em distribuidoras — exija que o DOT tenha no máximo 2 anos na data da compra.',
                },
                {
                  q: 'A validade muda se eu usar pouco o carro?',
                  a: 'Não. O envelhecimento da borracha ocorre independente do uso, por reações químicas com o oxigênio e o ozônio do ar. Um pneu com pouca rodagem de 8 anos ainda representa risco.',
                },
                {
                  q: 'Qual é a multa por pneu vencido?',
                  a: 'No Brasil, circular com pneu em mau estado de conservação é infração de natureza grave (5 pontos na CNH + multa), conforme o artigo 230 do CTB.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                  <p className="font-semibold text-white text-sm">{q}</p>
                  <p className="text-slate-400 text-sm mt-1 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </motion.article>

        {/* ── CTA download ── */}
        <ToolCta toolSlug={TOOL_SLUG} />
      </div>
    </main>

    <Footer />
  </>
);

export default DotPage;

